import { Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getAddressForSubmission, getInitialAddressValues } from '../../utils/address';
import { AddressSuggestion, LOADING_TYPES, useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { DynamicFieldAreaBaseProps, getInitialDynamicFieldValues, useDynamicFields } from '../../utils/dynamicFields';
import { getInitialDeliveryType } from '../../utils/getAvailableDeliveryTypes';
import getCarriers from '../../utils/getCarriers';
import { getCustomerType, pushErrorTrackingEventFromErrorObject } from '../../utils/tracking';
import MainHeadline from '../atoms/MainHeadline';
import AddressForm from '../molecules/AddressForm';
import CarrierOptions from '../molecules/CarrierOptions';
import CollectionPoints from '../molecules/CollectionPoints';
import DeliveryOptions from '../molecules/DeliveryOptions';
import Ingrid from '../molecules/Ingrid';
import Navigation from '../molecules/Navigation';
import { showDefaultError } from '../molecules/Notifications';

const ShippingStep = () => {
  const { checkoutState, setShippingAddress, setCollectionPoint, isLoading, setLoading, filledSlots } =
    useCheckoutState();
  const navigate = useNavigate();
  const { submitDynamicFieldArea, getCarrierCustomerId } = useDynamicFields();
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>();
  const reactRouterState = useLocation().state as { preselectedDeliveryType?: string };
  const isForwardToCollectionPointEnabled = config.deliveryOptions.forwardToCollectionPoint.enabled;
  const selectedCarrier = checkoutState.orderState.shipping?.selected?.[0];

  const isNewUser = getCustomerType(checkoutState) !== 'existing';
  const areShippingOptionsAvailable = !!checkoutState.orderOptions?.shipping?.homeDelivery?.length;
  const isEnabledAndShippingPosition =
    config.carrierOptions.isCollapsible.enabled && config.carrierOptions.position !== 'payment';
  const [isEditingCarriers, setEditingCarriers] = useState(
    isEnabledAndShippingPosition ? isNewUser || (areShippingOptionsAvailable && !selectedCarrier) : false
  );
  const { customer, orderOptions, orderState } = checkoutState;
  const isDeliveryOptionsSlotFilled = filledSlots.deliveryOptions;

  if (!customer) {
    return <Navigate to="/auth" replace />;
  }

  const isCustomDeliveryOption = config.features.ingrid.enabled || isDeliveryOptionsSlotFilled;
  const { carrierKey, carrierGroupKey, shippingOptionKey, deliveryDate } = selectedCarrier ?? {
    carrierKey: '',
    carrierGroupKey: '',
    shippingOptionKey: '',
    deliveryDate: ''
  };

  const initialDeliveryType = getInitialDeliveryType(
    orderState,
    orderOptions?.shipping,
    reactRouterState?.preselectedDeliveryType
  );

  const availableCountries = config.availableCountries.shipping.map(({ iso2Code }) => iso2Code);
  const initialCountry = availableCountries.find(country => country === config.country) ?? '';

  const initialValues = {
    deliveryType: initialDeliveryType,
    carrier: { carrierKey, carrierGroupKey, shippingOptionKey, deliveryDate },
    shippingAddress: {
      ...(orderState.addresses?.shipping && !orderState.addresses.shipping.collectionPoint ?
        getInitialAddressValues(orderState.addresses.shipping)
      : {
          country: initialCountry,
          gender: customer?.data.gender ?? 'f',
          title: customer?.data.title ?? '',
          firstName: orderState.addresses?.shipping?.firstName ?? customer?.data.firstName ?? '',
          lastName: orderState.addresses?.shipping?.lastName ?? customer?.data.lastName ?? '',
          streetHouseNumber: '',
          street: '',
          houseNumber: '',
          additional: '',
          zipCode: '',
          city: '',
          state: '',
          ...(isForwardToCollectionPointEnabled && { forwardToCollectionPoint: true })
        }),
      ...getInitialDynamicFieldValues(checkoutState, 'shippingAddress')
    },
    collectionPoint: {
      id: '',
      type: {
        carrierGroupKey: '',
        carrierKey: '',
        shippingOptionKey: '',
        collectionPointType: '',
        deliveryOptionKey: ''
      },
      additionalFields: getInitialDynamicFieldValues(checkoutState, 'collectionPoint')
    }
  };

  const onSubmit = async (values: typeof initialValues) => {
    setLoading(LOADING_TYPES.shippingSubmit);
    try {
      if (values.deliveryType === 'homeDelivery') {
        const _addressSuggestions = await setShippingAddress(getAddressForSubmission(values.shippingAddress));
        if (_addressSuggestions) {
          setAddressSuggestions(_addressSuggestions);
          setLoading(LOADING_TYPES.off);
          return;
        }
        await submitDynamicFieldArea(
          {
            area: 'shippingAddress',
            context: 'shippingAddress',
            conditionValues: {
              carrierGroupKey: getCarriers(checkoutState.orderState),
              shippingOptionKey: values.carrier?.shippingOptionKey,
              shippingCountry: values.shippingAddress.country
            }
          },
          values
        );
      } else if (
        (['storePickup', 'collectionPoint'] as (string | undefined)[]).includes(values.deliveryType) &&
        !!values.collectionPoint.id
      ) {
        const dynamicFieldArea: DynamicFieldAreaBaseProps = {
          area: 'collectionPoint',
          context: 'collectionPoint.additionalFields',
          conditionValues: {
            collectionPointType: values.collectionPoint.type.collectionPointType,
            carrierGroupKey: values.collectionPoint.type.carrierGroupKey,
            shippingOptionKey: values.collectionPoint.type.shippingOptionKey
          }
        };
        const carrierCustomerId = getCarrierCustomerId(dynamicFieldArea, values);
        await setCollectionPoint({
          specification: {
            collectionPointId: values.collectionPoint.id,
            ...(carrierCustomerId && { collectionPointCustomerNumber: carrierCustomerId }),
            customer: { firstName: values.shippingAddress.firstName, lastName: values.shippingAddress.lastName }
          },
          data: values.collectionPoint.type
        });
        await submitDynamicFieldArea(dynamicFieldArea, values);
      }
      navigate('/payment');
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'set_collection_point');
    }
    setLoading(LOADING_TYPES.off);
  };

  const isNavigationDisabled = ({ deliveryType, collectionPoint, shippingAddress }: typeof initialValues) => {
    if (isCustomDeliveryOption) {
      return undefined;
    } else if (!deliveryType) {
      return 'shipping.navigation.errors.noDeliveryType';
    } else if (!shippingAddress.country) {
      return 'shipping.navigation.errors.noShippingCountry';
    } else if (['collectionPoint', 'storePickup'].includes(deliveryType) && !collectionPoint.id) {
      return 'shipping.navigation.errors.noCollectionPointSelected';
    }

    return undefined;
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ values }) => (
        <>
          <div className="form shipping-form">
            <MainHeadline>
              <FormattedMessage id="shipping.headline" />
            </MainHeadline>
            <slot name="slot_replace_delivery_options">
              {isCustomDeliveryOption ?
                <Ingrid />
              : <DeliveryOptions />}
            </slot>
            {['collectionPoint', 'storePickup'].includes(values.deliveryType) && !isCustomDeliveryOption ?
              <CollectionPoints key={values.deliveryType} isStorePickup={values.deliveryType === 'storePickup'} />
            : <>
                {config.carrierOptions.position === 'shippingTop' && (
                  <CarrierOptions isEditingCarriers={isEditingCarriers} setEditingCarriers={setEditingCarriers} />
                )}
                <AddressForm
                  headline={<FormattedMessage id="address.shippingAddress" />}
                  context="shippingAddress"
                  availableCountries={availableCountries}
                  addressSuggestions={addressSuggestions}
                  setAddressSuggestions={setAddressSuggestions}
                />
                {config.carrierOptions.position === 'shippingBottom' && (
                  <CarrierOptions isEditingCarriers={isEditingCarriers} setEditingCarriers={setEditingCarriers} />
                )}
              </>
            }
            <Navigation
              nextStepLabel={
                ['collectionPoint', 'storePickup'].includes(values.deliveryType) && !!values.collectionPoint.id ?
                  <FormattedMessage id="shipping.collectionPoint.selectAndContinue" />
                : <FormattedMessage id="shipping.continueToPayment" />
              }
              disabledInformation={isNavigationDisabled(values)}
              isLoading={isLoading}
              step="shipping"
              onPreviousStepClick={
                checkoutState.orderState.addresses?.shipping ? () => navigate('/payment') : undefined
              }
            />

            {DEBUG && <pre style={{ marginTop: 60 }}>{JSON.stringify(values, null, 2)}</pre>}
          </div>
        </>
      )}
    </Formik>
  );
};

export default ShippingStep;
